import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Clock, Settings, Menu, X, LogOut, User, Shield, ShieldCheck, BookOpen, Presentation, GalleryHorizontalEnd, Layers, Palette, Bell, Users, Gift, HelpCircle, Zap, Sparkles, Check, Info, FileText, ChevronRight, ChevronDown, KeyRound, Mail } from "lucide-react";
import { useState, useEffect, useCallback, createContext, useContext } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SettingsModal from "@/components/SettingsModal";
import PageMeta from "@/components/PageMeta";
import NotificationBell from "@/components/NotificationBell";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import ConcurrencyLimitBanner from "@/components/ConcurrencyLimitBanner";

// Pages
import PPTGeneratorPage from "@/pages/PPTGeneratorPage";
import HistoryPage from "@/pages/HistoryPage";
import SettingsPage from "@/pages/SettingsPage";
import TraceabilityPage from "@/pages/TraceabilityPage";
import LoginPage from "@/pages/LoginPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PublicSharePage from "@/pages/PublicSharePage";
import FAQPage from "@/pages/FAQPage";
import AboutPage from "@/pages/AboutPage";
import AuthCallback from "@/pages/AuthCallback";
import BillingCompletePage from "@/pages/BillingCompletePage";
import BillingCancelPage from "@/pages/BillingCancelPage";
import UserManagementPage from "@/pages/UserManagementPage";
import AdminCreditsLedgerPage from "@/pages/AdminCreditsLedgerPage";
import AdminLogsPage from "@/pages/AdminLogsPage";
import GalleryConfigPage from "@/pages/GalleryConfigPage";
import ProfilePage from "@/pages/ProfilePage";
import AccountDetailsPage from "@/pages/AccountDetailsPage";
import HelpPage from "@/pages/HelpPage";
import GalleryPage from "@/pages/GalleryPage";
import UserDetailsPage from "@/pages/UserDetailsPage";
import AdminDetailsPage from "@/pages/AdminDetailsPage";
import PersonalizePage from "@/pages/PersonalizePage";
import AdminTemplateRequestsPage from "@/pages/AdminTemplateRequestsPage";
import AdminSupportRequestsPage from "@/pages/AdminSupportRequestsPage";
import AdminUserRequestsPage from "@/pages/AdminUserRequestsPage";
import AdminCommunicationsPage from "@/pages/AdminCommunicationsPage";
import AdminSecretsPage from "@/pages/AdminSecretsPage";
import AuditTimelinePage from "@/pages/AuditTimelinePage";
import ErrorBoundary from "@/components/ErrorBoundary";
import ReferralModal from "@/components/ReferralModal";
// Side-effect import: installs the global axios interceptor for friendly error toasts.
import "@/lib/api";

// API base — runtime-detected from the current page's origin so the same
// build works in preview AND production AND any custom domain. The backend
// is served from `/api/*` on the same domain via Emergent's ingress, so
// using `window.location.origin` keeps everything same-origin (no CORS,
// no SameSite cookie quirks, no `REACT_APP_BACKEND_URL` mismatch). The env
// var is honoured only as an explicit override (e.g. SSR/tests).
const _RUNTIME_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
export const API = process.env.REACT_APP_BACKEND_URL_FORCED
  ? `${process.env.REACT_APP_BACKEND_URL_FORCED}/api`
  : `${_RUNTIME_ORIGIN}/api`;

// Global axios interceptor: surface concurrent-runs cap (HTTP 429) as a
// dismissable top banner via the `concurrency:limit-hit` event. Other
// errors fall through unchanged so existing toast handling keeps working.
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 429) {
      const detail = err.response.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("concurrent")) {
        window.dispatchEvent(new CustomEvent("concurrency:limit-hit", {
          detail: { message: detail },
        }));
      }
    }
    return Promise.reject(err);
  }
);

// Template context to avoid re-fetching on every page
const TemplateContext = createContext({ templates: [], loading: true });
export const useTemplates = () => useContext(TemplateContext);

function TemplateProvider({ children }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/design-templates`)
      .then(res => setTemplates(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const refresh = () => {
    axios.get(`${API}/design-templates`)
      .then(res => setTemplates(res.data))
      .catch(() => {});
  };

  return (
    <TemplateContext.Provider value={{ templates, loading, refresh }}>
      {children}
    </TemplateContext.Provider>
  );
}

// User Menu Component
function UserMenu() {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const initial = (user.name?.[0] || user.email?.[0] || "?").toUpperCase();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          data-testid="user-menu"
        >
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">
              {initial}
            </div>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-gray-500" strokeWidth={2.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="end">
        <div className="px-2 py-2 border-b border-gray-100 mb-2">
          <p className="text-sm font-medium text-gray-800">{user.name}</p>
          <p className="text-xs text-gray-400">{user.email}</p>
          {isAdmin && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 mt-1">
              <Shield className="w-2.5 h-2.5" />Admin
            </span>
          )}
        </div>
        <NavLink
          to="/account"
          onClick={() => setOpen(false)}
          className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors"
          data-testid="account-details-link"
        >
          <Settings className="w-4 h-4" />
          Account Details
        </NavLink>
        <button
          onClick={() => { logout(); setOpen(false); }}
          className="w-full flex items-center gap-2 px-2 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </PopoverContent>
    </Popover>
  );
}

function TrialLowCreditsWatcher() {
  // Polls usage and surfaces a one-time low-credits nudge:
  //   • Trial accounts → toast when balance < 25 (existing behaviour)
  //   • Paid accounts (basic / pro / enterprise) → toast when balance < 50
  //     so users on a recurring plan still get a heads-up to top up.
  // Keys are persisted per-user in localStorage so the toast never re-fires
  // for the same threshold on the same balance.
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/usage`, {
        withCredentials: true,
        // Polling call — fires every 30s on the layout. Never surface
        // a 5xx toast; the next poll cycle will recover silently.
        _suppressToast: true,
      });
      setUsage(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchUsage();
    const t = setInterval(fetchUsage, 30_000);
    const onCreditsUpdate = () => fetchUsage();
    window.addEventListener("credits:refresh", onCreditsUpdate);
    return () => { clearInterval(t); window.removeEventListener("credits:refresh", onCreditsUpdate); };
  }, [user, fetchUsage]);

  useEffect(() => {
    if (!usage || !user || user.role === "admin") return;
    const balance = usage.credits_balance ?? 0;
    const isTrial = usage.account_type === "trial";
    const threshold = isTrial ? 25 : 50;
    if (balance >= threshold) return;
    const key = `wokelo_seen_low_credits_${isTrial ? "trial" : "paid"}_${user.user_id || user.email}`;
    if (typeof window === "undefined" || window.localStorage.getItem(key) === "true") return;
    window.localStorage.setItem(key, "true");
    if (isTrial) {
      // Trial users on low credits → push them to top-up (one-shot purchase
      // the user can complete in 30s) rather than a subscription commitment.
      // The sidebar's "Upgrade Plan" entry pulses concurrently as a
      // secondary nudge for users ready to commit to a recurring plan.
      toast("You're running low on free credits", {
        description: "Top up to keep generating.",
        duration: 10000,
        action: {
          label: "Buy credits",
          onClick: () => window.dispatchEvent(new CustomEvent("credits:open-upgrade", { detail: { tab: "topups" } })),
        },
      });
    } else {
      toast(`Only ${balance} credits left`, {
        description: "Top up now so your next deck doesn't stall.",
        duration: 10000,
        action: {
          label: "Buy credits",
          onClick: () => window.dispatchEvent(new CustomEvent("credits:open-upgrade", { detail: { tab: "topups" } })),
        },
      });
    }
  }, [usage, user]);

  return null;
}

function PlanBadge({ onUpgrade, onBuyCredits }) {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/usage`, {
        withCredentials: true,
        // Polling call — fires every 30s on the layout. Never surface
        // a 5xx toast; the next poll cycle will recover silently.
        _suppressToast: true,
      });
      setUsage(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchUsage();
    const t = setInterval(fetchUsage, 30_000);
    const onCreditsUpdate = () => fetchUsage();
    window.addEventListener("credits:refresh", onCreditsUpdate);
    return () => { clearInterval(t); window.removeEventListener("credits:refresh", onCreditsUpdate); };
  }, [user, fetchUsage]);

  // ── Trial low-credits nudge ───────────────────────────────────────────
  // Fire exactly once per user when a trial account drops below 25 credits.
  // Persistence via localStorage means it never re-fires across sessions for
  // the same account. Toast pushes the user to the Top-up tab (one-shot
  // purchase) rather than a subscription commitment — the sidebar's
  // "Upgrade Plan" entry pulses concurrently as a secondary nudge for users
  // ready to commit to a recurring plan.
  useEffect(() => {
    if (!usage || !user || user.role === "admin") return;
    if (usage.account_type !== "trial") return;
    const balance = usage.credits_balance ?? 0;
    if (balance >= 25) return;
    const key = `wokelo_seen_trial_low_credits_${user.user_id || user.email}`;
    if (typeof window === "undefined" || window.localStorage.getItem(key) === "true") return;
    window.localStorage.setItem(key, "true");
    toast("You're running low on free credits", {
      description: "Top up to keep generating.",
      duration: 10000,
      action: {
        label: "Buy credits",
        onClick: () => window.dispatchEvent(new CustomEvent("credits:open-upgrade", { detail: { tab: "topups" } })),
      },
    });
  }, [usage, user]);

  if (!usage || user?.role === "admin") return null;

  const isTrial = usage.account_type === "trial";
  const balance = usage.credits_balance ?? 0;
  const capacity = usage.credits_capacity || 1;
  const pct = Math.min(100, Math.round((balance / capacity) * 100));
  const isLow = pct <= 30;
  // Only render the badge while the user is actually running low — avoids
  // constantly nagging healthy paid accounts. Trial users always see it
  // because their capacity is one-shot and the upgrade nudge is the point.
  if (!isTrial && !isLow) return null;

  return (
    <div className={`plan-badge ${isTrial ? "trial" : "paid"}`} data-testid="plan-badge">
      <div className="plan-badge-row" data-testid="plan-badge-usage">
        <span className="plan-badge-balance">{balance.toLocaleString()}</span>
        <span className="plan-badge-suffix">
          {/* Trial users only see "X credits remaining" — never X/Y, so a
              top-up of e.g. 200 doesn't render as "200/50". */}
          {isTrial
            ? `credit${balance === 1 ? "" : "s"} remaining`
            : `credit${balance === 1 ? "" : "s"}`}
        </span>
      </div>
      <div className="plan-badge-actions">
        {/* Primary CTA is ALWAYS "Buy credits" — pushes users to a one-shot
            top-up purchase they can complete in 30s. The sidebar's
            "Upgrade Plan" entry pulses concurrently as a secondary nudge
            for users ready to commit to a recurring subscription. This
            two-CTA layout converts way better than a single "Upgrade plan"
            CTA which forces a higher-friction commitment decision. */}
        <button onClick={onBuyCredits} className="plan-badge-upgrade-btn" data-testid="plan-buy-credits-btn">
          <Zap className="w-3.5 h-3.5" />Buy credits
        </button>
      </div>
    </div>
  );
}

// Custom hook — returns true when the current user is "low on credits"
// (any tier). Used to illuminate the sidebar "Upgrade Plan" entry as a
// secondary nudge alongside the PlanBadge "Buy credits" primary CTA.
function useIsLowOnCredits() {
  const { user } = useAuth();
  const [low, setLow] = useState(false);

  useEffect(() => {
    if (!user || user.role === "admin") {
      setLow(false);
      return undefined;
    }
    let cancelled = false;
    const fetchAndDecide = async () => {
      try {
        const res = await axios.get(`${API}/me/usage`, {
          withCredentials: true,
          _suppressToast: true,
        });
        if (cancelled) return;
        const usage = res.data || {};
        const balance = usage.credits_balance ?? 0;
        const capacity = usage.credits_capacity || 1;
        const pct = Math.min(100, Math.round((balance / capacity) * 100));
        const isTrial = usage.account_type === "trial";
        // Same threshold the PlanBadge uses, so the two surfaces stay in
        // sync: trial users always low-flagged, paid users below 30%.
        setLow(isTrial || pct <= 30);
      } catch {
        // Silent — illumination is best-effort enrichment.
      }
    };
    fetchAndDecide();
    const t = setInterval(fetchAndDecide, 30_000);
    const onRefresh = () => fetchAndDecide();
    window.addEventListener("credits:refresh", onRefresh);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("credits:refresh", onRefresh);
    };
  }, [user]);

  return low;
}


function Sidebar({ open, onClose, onOpenSettings, onOpenUpgrade, onOpenTopups, onOpenRefer }) {
  const location = useLocation();
  const { isAdmin } = useAuth();
  // Drives the pulse highlight on the "Upgrade Plan" sidebar entry — fires
  // for trial users always, and paid users below 30% of their monthly
  // credits. Same threshold the PlanBadge uses so the two surfaces stay
  // in lock-step.
  const lowCredits = useIsLowOnCredits();

  useEffect(() => {
    onClose();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {open && <div className="sidebar-overlay" onClick={onClose} />}
      <aside className={`sidebar ${open ? "open" : ""}`} data-testid="sidebar">
        <div className="sidebar-logo">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-start">
              <img 
                src="https://customer-assets.emergentagent.com/job_0ff4c3b6-dbc6-4fa3-9f4e-495aa3aa78ed/artifacts/eubmytrj_wokelo-full%20logo-white-color%20%281%29.png" 
                alt="Wokelo" 
                className="h-5 mb-2"
              />
              <div>
                <span className="text-sm font-bold text-white tracking-tight font-['Manrope']">Slides by Wokelo</span>
              </div>
            </div>
            <button className="sidebar-close-btn" onClick={onClose} data-testid="sidebar-close">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
        <nav className="sidebar-nav">
          <NavLink to="/generator" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-generator">
            <Presentation className="w-4 h-4" />PPT Studio
          </NavLink>
          <NavLink to="/history" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-history">
            <Clock className="w-4 h-4" />History
          </NavLink>
          <NavLink to="/personalize" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-personalize">
            <Sparkles className="w-4 h-4" />Personalize
          </NavLink>
          <NavLink to="/gallery" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-gallery">
            <GalleryHorizontalEnd className="w-4 h-4" />Gallery
          </NavLink>

          {/* Admin Section */}
          {isAdmin && (
            <>
              <div className="px-4 py-2 mt-4">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Admin</span>
              </div>
              <NavLink to="/admin/users" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-admin-users">
                <User className="w-4 h-4" />Users
              </NavLink>
              <NavLink to="/admin/logs" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-admin-logs">
                <BookOpen className="w-4 h-4" />Logs
              </NavLink>
              <NavLink to="/admin/gallery-config" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-admin-gallery-config">
                <Palette className="w-4 h-4" />Gallery Config
              </NavLink>
              <NavLink to="/admin/user-requests" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-admin-user-requests">
                <FileText className="w-4 h-4" />User Requests
              </NavLink>
              <NavLink to="/admin/communications" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-admin-communications">
                <Mail className="w-4 h-4" />Communications
              </NavLink>
              <NavLink to="/admin/audit" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-admin-audit">
                <ShieldCheck className="w-4 h-4" />Audit Timeline
              </NavLink>
              <NavLink to="/admin/secrets" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-admin-secrets">
                <KeyRound className="w-4 h-4" />Secrets
              </NavLink>
              <NavLink to="/settings" className={({isActive}) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-settings">
                <Settings className="w-4 h-4" />Template Configs
              </NavLink>
            </>
          )}
        </nav>

        {/* Bottom actions — Upgrade Plan sits just above the Refer entry per
            user request. PlanBadge shows ONLY when the user is actually low
            on credits (paid <30% of capacity, or trial users by default) so
            healthy paid accounts aren't constantly nagged. */}
        <div className="sidebar-bottom">
          <PlanBadge onUpgrade={onOpenUpgrade} onBuyCredits={onOpenTopups} />
          <button
            type="button"
            onClick={() => onOpenUpgrade?.()}
            className={`sidebar-bottom-link ${lowCredits ? "sidebar-bottom-link-illuminated" : ""}`}
            data-testid="nav-upgrade-plan"
            data-illuminated={lowCredits ? "true" : "false"}
          >
            <Zap className="w-4 h-4" />Upgrade Plan
          </button>
          <button className="sidebar-bottom-link" onClick={onOpenRefer} data-testid="nav-refer">
            <Gift className="w-4 h-4" />Refer to earn credits
          </button>
          <NavLink to="/help" className={({isActive}) => `sidebar-bottom-link ${isActive ? "active" : ""}`} data-testid="nav-help">
            <HelpCircle className="w-4 h-4" />Help
          </NavLink>
        </div>
      </aside>
    </>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// Admin Route Component
function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/generator" replace />;
  }

  return children;
}

function UpgradeModal({ open, onClose, initialTab = "plans" }) {
  const { user } = useAuth();
  const [tab, setTab] = useState(initialTab); // "plans" | "topups"
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // "annual" | "monthly" (2026-05-26: monthly is the new default per product spec)
  const [plans, setPlans] = useState([]);
  const [topups, setTopups] = useState([]);
  const [billing, setBilling] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    (async () => {
      try {
        const [p, t, b] = await Promise.all([
          axios.get(`${API}/billing/plans`),
          axios.get(`${API}/billing/topups`),
          axios.get(`${API}/billing/me`, { withCredentials: true }),
        ]);
        setPlans(p.data.plans);
        setTopups(t.data.packages);
        setBilling(b.data);
      } catch {}
    })();
  }, [open, initialTab]);

  if (!open) return null;

  const purchaseTopup = async (pkg) => {
    setBusyId(pkg.id);
    try {
      // Real Stripe Checkout — backend creates a session, we redirect the user
      // to Stripe's hosted page. On return, /billing/complete polls for status
      // and grants credits.
      const session = await axios.post(`${API}/billing/checkout/topup`, {
        package_id: pkg.id,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (!session.data?.checkout_url) throw new Error("No checkout URL");
      window.location.href = session.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start checkout");
      setBusyId(null);
    }
    // Don't reset busyId on success — the page is unloading.
  };

  const subscribeTo = async (planId) => {
    if (planId === "enterprise") {
      window.location.href = "mailto:sales@wokelo.ai?subject=Wokelo%20Slides%20Enterprise%20inquiry";
      return;
    }
    setBusyId(planId);
    try {
      const session = await axios.post(`${API}/billing/checkout/subscribe`, {
        plan_id: planId,
        billing_period: billingPeriod,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (!session.data?.checkout_url) throw new Error("No checkout URL");
      window.location.href = session.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start checkout");
      setBusyId(null);
    }
  };

  const visiblePlans = plans.filter(p => p.id !== "trial");
  const currentPlanId = (billing?.account_type || user?.account_type || "trial");

  // Compute the maximum savings % across paid plans (annual vs monthly)
  const maxSavings = Math.max(0, ...visiblePlans
    .filter(p => p.monthly_price_usd && p.price_usd)
    .map(p => Math.round(((p.monthly_price_usd - p.price_usd) / p.monthly_price_usd) * 100)));

  const priceFor = (plan) => {
    if (plan.price_usd === null) return null;
    return billingPeriod === "annual" ? plan.price_usd : plan.monthly_price_usd;
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="upgrade-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full h-auto sm:h-[780px] max-h-[94vh] flex flex-col overflow-hidden border border-gray-100"
        onClick={e => e.stopPropagation()}
      >
        {/* Header — black/teal palette */}
        <div className="relative p-6 bg-gradient-to-br from-gray-950 via-gray-900 to-teal-950 text-white flex-shrink-0">
          <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/20 transition-colors" data-testid="upgrade-close">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-teal-400" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-300">Upgrade</span>
          </div>
          <h3 className="text-2xl font-bold tracking-tight">Power up your decks</h3>
          <p className="text-sm text-white/70 mt-1">
            {billing ? `Current plan: ${billing.plan?.name} · ${billing.credits_balance} credits` : "Pick a plan or top up credits"}
          </p>
          <div className="mt-4 inline-flex bg-white/10 rounded-lg p-1 border border-white/10">
            <button onClick={() => setTab("plans")} data-testid="tab-plans"
              className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${tab === "plans" ? "bg-white text-gray-900" : "text-white/70 hover:text-white"}`}>
              Subscription Plans
            </button>
            <button onClick={() => setTab("topups")} data-testid="tab-topups"
              className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${tab === "topups" ? "bg-white text-gray-900" : "text-white/70 hover:text-white"}`}>
              Top up Credits
            </button>
          </div>
        </div>

        {/* Body — vertical scroll on small screens; tall fixed height on desktop */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === "plans" && (
            <div data-testid="plans-grid">
              {/* Billing period toggle */}
              <div className="flex flex-col items-center mb-5">
                {maxSavings > 0 && (
                  <p className="text-xs font-semibold text-teal-600 mb-2" data-testid="annual-savings">
                    Save up to {maxSavings}% with annual billing
                  </p>
                )}
                <div className="inline-flex bg-gray-100 rounded-full p-1 border border-gray-200">
                  <button onClick={() => setBillingPeriod("monthly")} data-testid="billing-monthly"
                    className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${billingPeriod === "monthly" ? "bg-teal-600 text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                    Pay monthly
                  </button>
                  <button onClick={() => setBillingPeriod("annual")} data-testid="billing-annual"
                    className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${billingPeriod === "annual" ? "bg-teal-600 text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                    Pay annually
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {visiblePlans.map(p => {
                  const isPro = p.id === "pro";
                  const isCurrent = p.id === currentPlanId;
                  const price = priceFor(p);
                  return (
                    <div key={p.id}
                      className={`relative rounded-xl p-5 border-2 flex flex-col transition-all ${isPro ? "border-teal-500 bg-teal-50/30" : "border-gray-200"}`}
                      data-testid={`plan-card-${p.id}`}>
                      {isPro && (
                        <div className="absolute -top-3 right-4 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-teal-600 text-white rounded-full">
                          Most popular
                        </div>
                      )}
                      <div className="flex items-baseline justify-between mb-1">
                        <h4 className="text-lg font-bold text-gray-900">{p.name}</h4>
                        {isCurrent && <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-600">Current</span>}
                      </div>
                      <div className="text-3xl font-extrabold text-gray-900 mb-1">
                        {price === null ? "Custom" : <>${price}<span className="text-sm font-medium text-gray-400"> /mo</span></>}
                      </div>
                      {p.price_usd !== null && billingPeriod === "annual" && (
                        <div className="text-[11px] text-gray-500 mb-4">
                          Billed annually as ${p.annual_total_usd}
                        </div>
                      )}
                      {p.price_usd !== null && billingPeriod === "monthly" && (
                        <div className="text-[11px] text-gray-500 mb-4">
                          Month-to-month, cancel anytime
                        </div>
                      )}
                      {p.price_usd === null && (
                        <div className="text-[11px] text-gray-500 mb-4">
                          Custom pricing for your team
                        </div>
                      )}
                      <ul className="space-y-1.5 mb-5 flex-1">
                        {(p.features || []).map((f, i) => {
                          const text = typeof f === "string" ? f : f.text;
                          const tooltip = typeof f === "string" ? null : f.tooltip;
                          const subtext = typeof f === "string" ? null : f.subtext;
                          return (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                              <Check className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                              <span className="flex flex-col">
                                <span className="flex items-center gap-1">
                                  {text}
                                  {tooltip && (
                                    <span className="relative group inline-flex" data-testid={`feature-tooltip-${p.id}-${i}`}>
                                      <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10 px-2.5 py-1.5 rounded-md bg-gray-900 text-white text-[11px] font-medium whitespace-nowrap shadow-lg">
                                        {tooltip}
                                      </span>
                                    </span>
                                  )}
                                </span>
                                {/* Inline subtext rendered directly under the
                                    feature label — used to expand a feature
                                    name (e.g. data sources for Premium data)
                                    without leaning on the hover tooltip. */}
                                {subtext && (
                                  <span
                                    className="text-[11px] text-gray-500 leading-relaxed mt-0.5"
                                    data-testid={`feature-subtext-${p.id}-${i}`}
                                  >
                                    {subtext}
                                  </span>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      <button
                        onClick={() => subscribeTo(p.id)}
                        disabled={busyId === p.id || isCurrent}
                        className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                          isCurrent ? "bg-gray-100 text-gray-400 cursor-default" :
                          isPro ? "bg-teal-600 hover:bg-teal-700 text-white" :
                          "bg-gray-900 hover:bg-black text-white"
                        }`}
                        data-testid={`plan-cta-${p.id}`}
                      >
                        {busyId === p.id ? "Processing..." :
                         isCurrent ? "Current plan" :
                         p.id === "enterprise" ? "Contact sales" :
                         `Switch to ${p.name}`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "topups" && (
            <div data-testid="topups-grid" className="flex flex-col h-full">
              <p className="text-sm text-gray-500 mb-4">
                One-time credit purchases — bigger packs unlock bigger volume discounts.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-3">
                {topups.map(pkg => {
                  // Base rate is $0.50 per credit, so the "base credits" are
                  // 2× the dollar price. Anything over that is the bonus.
                  const baseCredits = pkg.price_usd * 2;
                  const bonus = pkg.credits - baseCredits;
                  const highlight = pkg.id === "topup_250";
                  return (
                    <div key={pkg.id}
                      className={`rounded-xl p-4 border-2 flex flex-col h-full ${highlight ? "border-teal-500 bg-teal-50/30" : "border-gray-200"}`}
                      data-testid={`topup-card-${pkg.id}`}>
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{pkg.label}</span>
                        {pkg.bonus_pct > 0 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 bg-teal-600 text-white rounded-full">
                            +{pkg.bonus_pct}%
                          </span>
                        )}
                      </div>
                      <div className="text-2xl font-extrabold text-gray-900 mb-1">${pkg.price_usd}</div>
                      <div className="text-sm text-gray-700 font-semibold">{pkg.credits.toLocaleString()} credits</div>
                      <div className="text-[11px] text-teal-600 mb-3 min-h-[16px]">
                        {bonus > 0 ? `${baseCredits} base + ${bonus} bonus` : "\u00A0"}
                      </div>
                      <button
                        onClick={() => purchaseTopup(pkg)}
                        disabled={busyId === pkg.id}
                        className="mt-auto w-full py-2 rounded-lg bg-gray-900 hover:bg-black text-white text-sm font-semibold transition-colors disabled:opacity-50"
                        data-testid={`topup-cta-${pkg.id}`}
                      >
                        {busyId === pkg.id ? "Processing..." : `Buy for $${pkg.price_usd}`}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Conversion banner intentionally removed (2026-05-07 — user
                  request) so top-up users aren't nudged toward switching
                  plans on the credit pack tab. */}

              <p className="text-[11px] text-gray-400 mt-3 text-center">
                Secure checkout powered by Stripe — test cards work without real charges.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [upgradeTab, setUpgradeTab] = useState("plans");
  const { user, impersonation } = useAuth();

  useEffect(() => {
    // Existing callers (PPTGeneratorPage, PersonalizePage, UserDetailsPage,
    // NotificationBell) dispatch this event without a payload — keep their
    // historical behavior of opening the Top-up tab. New callers can pass
    // `{detail: {tab: "plans"}}` to land on the Subscription Plans tab.
    const open = (e) => {
      const tab = e?.detail?.tab === "plans" ? "plans" : "topups";
      setUpgradeTab(tab);
      setUpgradeOpen(true);
    };
    window.addEventListener("credits:open-upgrade", open);
    // Generic hook so any page (e.g. ReferralCelebrationBanner on /history)
    // can open the refer-a-friend modal without prop-drilling.
    const openRefer = () => setReferralOpen(true);
    window.addEventListener("referral:open", openRefer);
    return () => {
      window.removeEventListener("credits:open-upgrade", open);
      window.removeEventListener("referral:open", openRefer);
    };
  }, []);

  return (
    <div className={`app-layout ${impersonation?.active ? "is-impersonating" : ""}`}>
      {/* All authenticated app pages are private — opt them out of search
          indexing globally. Mounting <PageMeta noindex/> here flips the
          `<meta name="robots">` tag at runtime for every protected route. */}
      <PageMeta noindex />
      <ImpersonationBanner />
      <ConcurrencyLimitBanner />
      <TrialLowCreditsWatcher />
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenUpgrade={() => { setUpgradeTab("plans"); setUpgradeOpen(true); }}
        onOpenTopups={() => { setUpgradeTab("topups"); setUpgradeOpen(true); }}
        onOpenRefer={() => setReferralOpen(true)}
      />

      <main className="main-content">
        {/* Top Bar with Bell + User Menu */}
        <div className="fixed top-0 right-0 z-40 flex items-center gap-2 p-3 md:pr-6">
          {/* Notification Bell */}
          <NotificationBell apiBase={API} />
          <UserMenu />
          {/* Mobile burger */}
          <button
            className="mobile-burger-inline md:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="burger-menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        <div className="pt-14">
          <Routes>
            <Route path="/generator" element={<ProtectedRoute><PPTGeneratorPage /></ProtectedRoute>} />
            <Route path="/personalize" element={<ProtectedRoute><PersonalizePage /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
            <Route path="/gallery" element={<ProtectedRoute><GalleryPage /></ProtectedRoute>} />
            <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
            <Route path="/traceability/:type/:id" element={<ProtectedRoute><TraceabilityPage /></ProtectedRoute>} />
            
            {/* User Details - for History view (no cost info) */}
            <Route path="/details/:type/:id" element={<ProtectedRoute><UserDetailsPage /></ProtectedRoute>} />
            
            {/* Admin Routes */}
            <Route path="/admin/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
            <Route path="/admin/users/:userId/credits" element={<AdminRoute><AdminCreditsLedgerPage /></AdminRoute>} />
            <Route path="/admin/template-requests" element={<AdminRoute><AdminTemplateRequestsPage /></AdminRoute>} />
            <Route path="/admin/support-requests" element={<AdminRoute><AdminSupportRequestsPage /></AdminRoute>} />
            <Route path="/admin/user-requests" element={<AdminRoute><AdminUserRequestsPage /></AdminRoute>} />
            <Route path="/admin/communications" element={<AdminRoute><AdminCommunicationsPage /></AdminRoute>} />
            <Route path="/admin/audit" element={<AdminRoute><AuditTimelinePage /></AdminRoute>} />
            <Route path="/admin/logs" element={<AdminRoute><AdminLogsPage /></AdminRoute>} />
            <Route path="/admin/gallery-config" element={<AdminRoute><GalleryConfigPage /></AdminRoute>} />
            <Route path="/admin/secrets" element={<AdminRoute><AdminSecretsPage /></AdminRoute>} />
            
            {/* Admin Details - for Admin Logs view (with cost/token info) */}
            <Route path="/admin/details/:type/:id" element={<AdminRoute><AdminDetailsPage /></AdminRoute>} />
            
            {/* Profile */}
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><AccountDetailsPage /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
            
            {/* Redirect root to generator */}
            <Route path="/" element={<Navigate to="/generator" replace />} />
          </Routes>
        </div>
      </main>

      {/* Settings Modal */}
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      {/* Upgrade Modal */}
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} initialTab={upgradeTab} />
      <ReferralModal open={referralOpen} onClose={() => setReferralOpen(false)} />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback) - must be synchronous
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<LoginPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/billing/complete" element={<BillingCompletePage />} />
      <Route path="/billing/cancel" element={<BillingCancelPage />} />
      <Route path="/share/:token" element={<PublicSharePage />} />
      <Route path="/*" element={<AppLayout />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AuthProvider>
          <TemplateProvider>
            <AppRouter />
          </TemplateProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
